/*
 * File: onboarding.slice
 * Keep track of the onboarding multi-step process.
 *
 * The slice is connected with the RTK Query (onboardingApi) to unify shared statuses among the three main processes.
 * - start: RTK has its own api for the start process (loading, error, data ...)
 * - next: RTK has its own api for the next process (loading, error, data ...)
 * - back: RTK has its own api for the back process (loading, error, data ...)
 *
 * This slice derive the statuses from the 3 APIs above, merging the same statuses (eg. the 3 loading statuses) into a single one.
 */
import { isAnyOf, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  allOnboardingFulfilledMatchers,
  allOnboardingPendingMatchers,
  allOnboardingRejectedMatchers,
  onboardingApi,
} from "../../api/onboardingApi";
import { PageContent } from "../../types/components.type";
import { RedirectErrorData } from "../../types/error.type";
import {
  createGenericState,
  onFulfilledMatcher,
  onPendingMatcher,
  onRejectMatcher,
} from "../genericState/genericState";
import { ScaErrorType } from "../../providers/FlutterProvider/types";

export type OnboardingState = {
  isLoading: boolean;
  processId?: string;
  isError: boolean;
  isSuccess: boolean;
  isUninitialized: boolean;
  error: SerializedError | FetchBaseQueryError | ScaErrorType | undefined;
  errorCode?: number;
  data?: PageContent;
  currentStep: number;
  lastAction?: string;
  stepValues?: Record<string, unknown>;
  redirectError?: RedirectErrorData;
};

export const initialState: OnboardingState = {
  isLoading: false,
  isError: false,
  isSuccess: false,
  isUninitialized: true,
  error: undefined,
  data: undefined,
  currentStep: 0,
  redirectError: undefined,
};

export const onboardingSlice = createGenericState("onboarding", initialState, {
  reducers: {
    reset() {
      return initialState;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(
        onboardingApi.endpoints.next.matchPending,
        (state, action) => {
          state.stepValues = action.meta.arg.originalArgs.values;
        },
      )
      .addMatcher(isAnyOf(...allOnboardingPendingMatchers), (state, action) => {
        return onPendingMatcher(state, action);
      })
      .addMatcher(
        isAnyOf(...allOnboardingRejectedMatchers),
        (state, action) => {
          onRejectMatcher(state, action);
        },
      )
      .addMatcher(
        isAnyOf(...allOnboardingFulfilledMatchers),
        (state, action) => {
          onFulfilledMatcher(state, action);
        },
      );
  },
});

export const onboardingActions = {
  ...onboardingSlice.actions,
};
export default onboardingSlice;
